import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { Button, Spinner, Badge, Alert, AlertDescription } from '@/components/ui';
import { ConfirmDialog, DeleteConfirmDialog } from '@/components/ui';
import { Smartphone, Mail, KeyRound, ShieldCheck, Trash2, Plus, LifeBuoy, Monitor } from 'lucide-react';
import MfaEnrollFlow from './MfaEnrollFlow';
import MfaChallenge from './MfaChallenge';
import RecoveryCodesDisplay from './RecoveryCodesDisplay';
import {
  getMfaStatus, removeFactor, generateRecoveryCodes, listTrustedDevices, revokeTrustedDevice,
  type MfaFactor, type MfaMethod, type TrustedDevice,
} from '@/services/mfaService';
import { mfaTokenStore } from '@/services/mfaTokenStore';
import { useDateFormat } from '@/hooks/useDateFormat';

const ICONS: Record<MfaMethod, typeof Smartphone> = { totp: Smartphone, email: Mail, passkey: KeyRound };

/** What the user was doing when we stopped to ask them to prove it's them. */
type PendingAction = { kind: 'add' } | { kind: 'remove'; factor: MfaFactor } | { kind: 'recovery-codes' };

export default function TwoFactorPanel() {
  const { activeOrganizationId, onMfaSatisfied, refreshMfaSession } = useAuth();
  const { success, error } = useToast();
  const fmt = useDateFormat();
  const orgId = activeOrganizationId ?? undefined;

  const [loading, setLoading] = useState(true);
  const [factors, setFactors] = useState<MfaFactor[]>([]);
  const [orgMode, setOrgMode] = useState<'off' | 'allowed' | 'required'>('off');
  const [recoveryRemaining, setRecoveryRemaining] = useState(0);
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [adding, setAdding] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<MfaFactor | null>(null);
  // Trusted device waiting for confirmation in the DeleteConfirmDialog
  const [pendingDevice, setPendingDevice] = useState<TrustedDevice | null>(null);
  const [stepUpFor, setStepUpFor] = useState<PendingAction | null>(null);
  const [freshCodes, setFreshCodes] = useState<string[] | null>(null);

  const load = useCallback(async () => {
    try {
      const [s, d] = await Promise.all([
        getMfaStatus(orgId),
        listTrustedDevices(orgId).catch(() => ({ devices: [] as TrustedDevice[] })),
      ]);
      setFactors(s.factors);
      setOrgMode(s.orgPolicy.mode);
      setRecoveryRemaining(s.recoveryCodes?.remaining ?? 0);
      setDevices(d.devices);
    } catch (e: any) {
      error(e?.message || 'Could not load two-factor settings');
    } finally {
      setLoading(false);
    }
  }, [orgId, error]);

  useEffect(() => { void load(); }, [load]);

  /**
   * Every security-settings change needs a step-up token that only a fresh
   * challenge mints — so with any factor enrolled we always challenge first,
   * rather than depending on login state, org membership, or device trust. That
   * uniformity is the point: there's no user shape that takes a different path.
   */
  const run = (action: PendingAction) => {
    if (factors.length > 0 && !mfaTokenStore.getSudo()) { setStepUpFor(action); return; }
    void perform(action);
  };

  const perform = async (action: PendingAction) => {
    if (action.kind === 'add') { setAdding(true); return; }
    if (action.kind === 'recovery-codes') {
      try {
        const r = await generateRecoveryCodes(orgId);
        setFreshCodes(r.codes);
      } catch (e: any) {
        if (e?.code === 'mfa_required') setStepUpFor(action);
        else error(e?.message || 'Could not generate recovery codes');
      }
      return;
    }
    try {
      await removeFactor(action.factor.id, orgId);
      success('Removed');
      await load();
      void refreshMfaSession();
    } catch (e: any) {
      if (e?.code === 'mfa_required') setStepUpFor(action);
      else error(e?.message || 'Could not remove that method');
    }
  };

  const onStepUpVerified = (token: string, exp: number, sudo: string, sudoExp: number) => {
    const resume = stepUpFor;
    onMfaSatisfied(token, exp);
    mfaTokenStore.setSudo(sudo, sudoExp);
    setStepUpFor(null);
    if (resume) void perform(resume);
  };

  const revokeDevice = async (hash: string) => {
    try {
      await revokeTrustedDevice(hash, orgId);
      success('Device removed');
      await load();
    } catch (e: any) {
      error(e?.message || 'Could not remove that device');
    }
  };

  if (loading) return <div className="flex justify-center py-10"><Spinner size="lg" /></div>;

  const enrolledTypes = Array.from(new Set(factors.map((f) => f.type)));

  // Show-once codes take over the panel — nothing else matters until they're saved.
  if (freshCodes) {
    return (
      <div className="max-w-xl">
        <RecoveryCodesDisplay
          codes={freshCodes}
          onDone={() => { setFreshCodes(null); void load(); }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-xl">
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-muted-foreground mt-0.5" />
        <div>
          <h2 className="text-lg font-semibold text-foreground">Two-factor authentication</h2>
          <p className="text-sm text-muted-foreground">
            Add a second step at sign-in. {orgMode === 'required'
              ? 'Your organization requires two-factor authentication.'
              : orgMode === 'allowed'
                ? 'Your organization has enabled two-factor authentication for members.'
                : 'Protect your account with an authenticator app, passkey, or email code.'}
          </p>
        </div>
      </div>

      {/* Enrolled with no way back in is the one state that strands people. */}
      {factors.length > 0 && recoveryRemaining === 0 && (
        <Alert variant="destructive">
          <AlertDescription>
            You have no recovery codes. If you lose access to your methods below, you won't be able to sign in.
          </AlertDescription>
        </Alert>
      )}

      {factors.length > 0 ? (
        <div className="space-y-2">
          {factors.map((f) => {
            const Icon = ICONS[f.type];
            return (
              <div key={f.id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card">
                <Icon className="w-5 h-5 text-muted-foreground" />
                <div className="flex-1">
                  <div className="font-medium text-foreground">{f.label}</div>
                  <div className="text-xs text-muted-foreground">
                    Added {f.createdAt ? fmt.formatDate(f.createdAt) : '—'}
                    {f.lastUsedAt ? ` · Last used ${fmt.formatDate(f.lastUsedAt)}` : ''}
                  </div>
                </div>
                <button onClick={() => setConfirmRemove(f)} className="text-muted-foreground hover:text-status-error p-2" aria-label="Remove">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="p-4 rounded-lg border border-dashed border-border text-sm text-muted-foreground">
          You don't have any second factors yet.
          {orgMode === 'required' && <Badge variant="secondary" className="ml-2">Required by your org</Badge>}
        </div>
      )}

      {stepUpFor ? (
        <div className="p-4 rounded-lg border border-border bg-card space-y-4">
          <div>
            <h3 className="font-medium text-foreground">Confirm it's you</h3>
            <p className="text-sm text-muted-foreground">
              Verify with a method you already have before changing your two-factor settings.
            </p>
          </div>
          <MfaChallenge
            orgId={orgId}
            methods={enrolledTypes}
            recoveryAvailable={recoveryRemaining > 0}
            onVerified={onStepUpVerified}
          />
          <Button variant="ghost" className="w-full" onClick={() => setStepUpFor(null)}>Cancel</Button>
        </div>
      ) : adding ? (
        <div className="p-4 rounded-lg border border-border bg-card">
          <MfaEnrollFlow
            orgId={orgId}
            existing={enrolledTypes}
            onEnrolled={(r) => {
              setAdding(false);
              success('Two-factor method added');
              if (r.mfaToken && r.expiresAtMs) onMfaSatisfied(r.mfaToken, r.expiresAtMs);
              if (r.sudoToken) mfaTokenStore.setSudo(r.sudoToken, r.sudoExpiresAtMs);
              // First factor ships a code set — show it before anything else.
              if (r.recoveryCodes?.length) setFreshCodes(r.recoveryCodes);
              void load();
            }}
            onCancel={() => setAdding(false)}
            onStepUpRequired={() => { setAdding(false); setStepUpFor({ kind: 'add' }); }}
          />
        </div>
      ) : (
        enrolledTypes.length < 3 && (
          <Button variant="secondary" onClick={() => run({ kind: 'add' })}>
            <Plus className="w-4 h-4 mr-2" /> Add a method
          </Button>
        )
      )}

      {factors.length > 0 && (
        <div className="space-y-3 pt-2 border-t border-border">
          <div className="flex items-start gap-3">
            <LifeBuoy className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-foreground">Recovery codes</h3>
              <p className="text-sm text-muted-foreground">
                {recoveryRemaining > 0
                  ? `${recoveryRemaining} unused ${recoveryRemaining === 1 ? 'code' : 'codes'} remaining. Generating a new set replaces them all.`
                  : 'Single-use codes that get you in when your other methods are unavailable.'}
              </p>
            </div>
          </div>
          <Button variant="secondary" onClick={() => run({ kind: 'recovery-codes' })}>
            {recoveryRemaining > 0 ? 'Generate new codes' : 'Generate recovery codes'}
          </Button>
        </div>
      )}

      {devices.length > 0 && (
        <div className="space-y-3 pt-2 border-t border-border">
          <div className="flex items-start gap-3">
            <Monitor className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div>
              <h3 className="font-medium text-foreground">Trusted devices</h3>
              <p className="text-sm text-muted-foreground">
                These skip the sign-in challenge for 30 days. They never authorize changes on this page.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {devices.map((d) => (
              <div key={d.hash} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-foreground truncate">{describeDevice(d.userAgent)}</div>
                  <div className="text-xs text-muted-foreground">
                    Trusted {d.createdAt ? fmt.formatDate(d.createdAt) : '—'}
                    {d.expiresAt ? ` · Expires ${fmt.formatDate(d.expiresAt)}` : ''}
                  </div>
                </div>
                <button onClick={() => setPendingDevice(d)} className="text-muted-foreground hover:text-status-error p-2" aria-label="Revoke device">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        title="Remove this method?"
        description="You'll no longer be able to use it to verify your identity."
        confirmText="Remove"
        variant="destructive"
        onConfirm={() => {
          const f = confirmRemove;
          setConfirmRemove(null);
          if (f) run({ kind: 'remove', factor: f });
        }}
      />

      <DeleteConfirmDialog
        open={pendingDevice !== null}
        onClose={() => setPendingDevice(null)}
        title="Remove trusted device?"
        verb="remove"
        confirmText="Remove"
        itemName={pendingDevice ? describeDevice(pendingDevice.userAgent) : undefined}
        details="It will be asked for 2FA at its next sign-in."
        onConfirm={async () => {
          if (pendingDevice) await revokeDevice(pendingDevice.hash);
        }}
      />
    </div>
  );
}

/** Best-effort human label — the raw UA string is unreadable in a list. */
function describeDevice(ua: string | null): string {
  if (!ua) return 'Unknown device';
  const os = /Android/i.test(ua) ? 'Android'
    : /iPhone|iPad|iOS/i.test(ua) ? 'iOS'
    : /Mac OS X/i.test(ua) ? 'Mac'
    : /Windows/i.test(ua) ? 'Windows'
    : /Linux/i.test(ua) ? 'Linux'
    : 'Unknown OS';
  const browser = /Edg\//i.test(ua) ? 'Edge'
    : /Chrome\//i.test(ua) ? 'Chrome'
    : /Safari\//i.test(ua) ? 'Safari'
    : /Firefox\//i.test(ua) ? 'Firefox'
    : 'Browser';
  return `${browser} on ${os}`;
}
